import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import queryString from 'api/utils/queryStringBuilder';
import {
  Rule,
  CreateRuleInput,
  EditRuleInput,
  isRule,
} from '@billing/validation/rules';
import {
  getRequest as get,
  postRequest as post,
  putRequest as put,
  deleteRequest as del,
} from '../utils/HttpMethods';
import { useListQuery, useMutationQuery } from '../utils/queries';
import { compareDateDesc } from 'utils/dateUtils';

export const ruleQueryKeys = {
  all: ['rules'],
  siteRoot: (siteID: string) => [...ruleQueryKeys.all, 'site', siteID],
  ruleSetRoot: (ruleSetID: string) => [
    ...ruleQueryKeys.all,
    'ruleSet',
    ruleSetID,
  ],
  site: (
    siteID: string,
    status?: Rule['status'],
    active?: boolean
  ): string[] => [
    ...ruleQueryKeys.siteRoot(siteID),
    status || 'all',
    typeof active === 'boolean' ? String(active) : 'all',
  ],
  siteRuleSets: (
    siteID: string,
    status?: Rule['status'],
    active?: boolean
  ): string[] => [...ruleQueryKeys.site(siteID, status, active), 'ruleSets'],
  ruleSet: (ruleSetID: string): string[] => [
    ...ruleQueryKeys.ruleSetRoot(ruleSetID),
  ],
};

export const getRuleSetSnapshot = (rules: Rule[]): Rule[] => {
  const byRuleSetID = new Map<string, Rule[]>();

  for (const rule of rules) {
    const versions = byRuleSetID.get(rule.ruleSetID);
    if (versions) {
      versions.push(rule);
      continue;
    }
    byRuleSetID.set(rule.ruleSetID, [rule]);
  }

  const selectNewest = (versions: Rule[]): Rule => {
    return versions.reduce((latest, current) => {
      if (!latest) return current;

      if (current.version !== latest.version) {
        return current.version > latest.version ? current : latest;
      }

      return compareDateDesc(current.createdAt, latest.createdAt) < 0
        ? current
        : latest;
    }, versions[0]);
  };

  const selectedByRuleSetID = new Map<string, Rule>();

  for (const [ruleSetID, versions] of byRuleSetID.entries()) {
    const operationalVersions = versions.filter(
      (version) => version.active || version.status === 'published'
    );

    if (operationalVersions.length > 0) {
      selectedByRuleSetID.set(ruleSetID, selectNewest(operationalVersions));
      continue;
    }

    selectedByRuleSetID.set(ruleSetID, selectNewest(versions));
  }

  return Array.from(selectedByRuleSetID.values()).sort((left, right) => {
    if (right.version !== left.version) return right.version - left.version;
    return compareDateDesc(left.createdAt, right.createdAt);
  });
};

export const listRulesBySite = async ({
  siteID,
  status,
  active,
}: {
  siteID: string;
  status?: Rule['status'];
  active?: boolean;
}): Promise<Rule[]> => {
  return await get<Rule[]>(
    'QBilling',
    `/resource/rules/site${queryString({
      siteID,
      status,
      active: typeof active === 'boolean' ? String(active) : undefined,
    })}`
  );
};

export const listRulesByRuleSet = async ({
  ruleSetID,
}: {
  ruleSetID: string;
}): Promise<Rule[]> => {
  return await get<Rule[]>(
    'QBilling',
    `/resource/rules/ruleSet${queryString({
      ruleSetID,
    })}`
  );
};

export const useListRulesBySite = ({
  siteID,
  status,
  active,
  displayNotification = false,
}: {
  siteID?: string;
  status?: Rule['status'];
  active?: boolean;
  displayNotification?: boolean;
}): UseQueryResult<Rule[], Error> => {
  return useListQuery<Rule>({
    queryKey: ruleQueryKeys.site(siteID || 'missing-site', status, active),
    fetchFunction: listRulesBySite,
    functionName: 'listRulesBySite',
    params: {
      siteID,
      status,
      active,
    },
    isEntity: isRule,
    enabled: Boolean(siteID),
    displayNotification,
  });
};

export const listRuleSetsBySite = async ({
  siteID,
  status,
  active,
}: {
  siteID: string;
  status?: Rule['status'];
  active?: boolean;
}): Promise<Rule[]> => {
  const rules = await listRulesBySite({
    siteID,
    status,
    active,
  });

  return getRuleSetSnapshot(rules);
};

export const useListRuleSetsBySite = ({
  siteID,
  status,
  active,
  displayNotification = false,
}: {
  siteID?: string;
  status?: Rule['status'];
  active?: boolean;
  displayNotification?: boolean;
}): UseQueryResult<Rule[], Error> => {
  return useListQuery<Rule>({
    queryKey: ruleQueryKeys.siteRuleSets(
      siteID || 'missing-site',
      status,
      active
    ),
    fetchFunction: listRuleSetsBySite,
    functionName: 'listRuleSetsBySite',
    params: {
      siteID,
      status,
      active,
    },
    isEntity: isRule,
    enabled: Boolean(siteID),
    displayNotification,
  });
};

export const useListRulesByRuleSet = ({
  ruleSetID,
  displayNotification = false,
}: {
  ruleSetID?: string;
  displayNotification?: boolean;
}): UseQueryResult<Rule[], Error> => {
  return useListQuery<Rule>({
    queryKey: ruleQueryKeys.ruleSet(ruleSetID || 'missing-rule-set'),
    fetchFunction: listRulesByRuleSet,
    functionName: 'listRulesByRuleSet',
    params: {
      ruleSetID,
    },
    isEntity: isRule,
    enabled: Boolean(ruleSetID),
    displayNotification,
  });
};

export const createRule = async (rule: CreateRuleInput): Promise<Rule> => {
  const created = await post<Rule, CreateRuleInput>(
    'QBilling',
    '/resource/rules',
    rule
  );
  if (!created) {
    throw new Error('createRule Failed');
  }
  return created;
};

const getLatestRuleVersion = async (ruleSetID: string): Promise<Rule> => {
  const versions = await listRulesByRuleSet({ ruleSetID });
  if (!Array.isArray(versions) || versions.length === 0) {
    throw new Error('No rule versions found for the supplied ruleSetID.');
  }

  return versions[0];
};

type RuleVersionInput = Pick<EditRuleInput, 'ruleSetID' | 'lastUpdatedBy'>;
type RuleVersionByIDInput = Pick<Rule, 'id' | 'lastUpdatedBy'>;

export const createRuleVersion = async (
  input: EditRuleInput
): Promise<Rule> => {
  const { ruleSetID, lastUpdatedBy, name, body } = input;

  if (typeof name === 'undefined' && typeof body === 'undefined') {
    throw new Error(
      'At least one of name or body is required to create a new version.'
    );
  }

  const latestVersion = await getLatestRuleVersion(ruleSetID);
  const created = await post<
    Rule,
    {
      id: string;
      lastUpdatedBy: string;
      name?: Rule['name'];
      body?: Rule['body'];
    }
  >('QBilling', '/resource/rules/version', {
    id: latestVersion.id,
    lastUpdatedBy,
    name,
    body,
  });

  if (!created) {
    throw new Error('createRuleVersion Failed');
  }

  return created;
};

export const activateLatestRuleVersion = async ({
  ruleSetID,
  lastUpdatedBy,
}: RuleVersionInput): Promise<Rule> => {
  const latestVersion = await getLatestRuleVersion(ruleSetID);
  const activated = await put<Rule, { id: string; lastUpdatedBy: string }>(
    'QBilling',
    '/resource/rules/version/activate',
    {
      id: latestVersion.id,
      lastUpdatedBy,
    }
  );

  if (!activated) {
    throw new Error('activateLatestRuleVersion Failed');
  }

  return activated;
};

export const activateRuleVersion = async ({
  id,
  lastUpdatedBy,
}: RuleVersionByIDInput): Promise<Rule> => {
  const activated = await put<Rule, { id: string; lastUpdatedBy: string }>(
    'QBilling',
    '/resource/rules/version/activate',
    {
      id,
      lastUpdatedBy,
    }
  );

  if (!activated) {
    throw new Error('activateRuleVersion Failed');
  }

  return activated;
};

export const archiveLatestRuleVersion = async ({
  ruleSetID,
  lastUpdatedBy,
}: RuleVersionInput): Promise<Rule> => {
  const latestVersion = await getLatestRuleVersion(ruleSetID);
  const archived = await put<Rule, { id: string; lastUpdatedBy: string }>(
    'QBilling',
    '/resource/rules/version/archive',
    {
      id: latestVersion.id,
      lastUpdatedBy,
    }
  );

  if (!archived) {
    throw new Error('archiveLatestRuleVersion Failed');
  }

  return archived;
};

export const archiveRuleVersion = async ({
  id,
  lastUpdatedBy,
}: RuleVersionByIDInput): Promise<Rule> => {
  const archived = await put<Rule, { id: string; lastUpdatedBy: string }>(
    'QBilling',
    '/resource/rules/version/archive',
    {
      id,
      lastUpdatedBy,
    }
  );

  if (!archived) {
    throw new Error('archiveRuleVersion Failed');
  }

  return archived;
};

export const deleteRuleSet = async ({
  ruleSetID,
}: {
  ruleSetID: string;
}): Promise<{ deleted: number }> => {
  const deleted = await del<{ deleted: number }, { ruleSetID: string }>(
    'QBilling',
    '/resource/rules/ruleSet',
    { ruleSetID }
  );

  if (!deleted) {
    throw new Error('deleteRuleSet Failed');
  }

  return deleted;
};

export const useCreateRule = ({
  siteID,
}: {
  siteID?: string;
}): UseMutationResult<Rule, Error> => {
  return useMutationQuery<Rule>({
    mutationFn: createRule,
    successMessage: 'Rule created successfully.',
    errorMessage: 'Could not create rule.',
    queryKey: siteID ? ruleQueryKeys.siteRoot(siteID) : ruleQueryKeys.all,
  });
};

export const useCreateRuleVersion = ({
  siteID,
}: {
  siteID?: string;
}): UseMutationResult<Rule, Error> => {
  return useMutationQuery<Rule>({
    mutationFn: createRuleVersion,
    successMessage: 'Rule version created successfully.',
    errorMessage: 'Could not create rule version.',
    queryKey: siteID ? ruleQueryKeys.siteRoot(siteID) : ruleQueryKeys.all,
  });
};

export const useActivateLatestRuleVersion = ({
  siteID,
}: {
  siteID?: string;
}): UseMutationResult<Rule, Error> => {
  return useMutationQuery<Rule>({
    mutationFn: activateLatestRuleVersion,
    successMessage: 'Rule version published successfully.',
    errorMessage: 'Could not publish rule version.',
    queryKey: siteID ? ruleQueryKeys.siteRoot(siteID) : ruleQueryKeys.all,
  });
};

export const useActivateRuleVersion = ({
  siteID,
}: {
  siteID?: string;
}): UseMutationResult<Rule, Error> => {
  return useMutationQuery<Rule>({
    mutationFn: activateRuleVersion,
    successMessage: 'Rule activated successfully.',
    errorMessage: 'Could not activate rule.',
    queryKey: siteID ? ruleQueryKeys.siteRoot(siteID) : ruleQueryKeys.all,
  });
};

export const useArchiveLatestRuleVersion = ({
  siteID,
}: {
  siteID?: string;
}): UseMutationResult<Rule, Error> => {
  return useMutationQuery<Rule>({
    mutationFn: archiveLatestRuleVersion,
    successMessage: 'Rule version archived successfully.',
    errorMessage: 'Could not archive rule version.',
    queryKey: siteID ? ruleQueryKeys.siteRoot(siteID) : ruleQueryKeys.all,
  });
};

export const useArchiveRuleVersion = ({
  siteID,
}: {
  siteID?: string;
}): UseMutationResult<Rule, Error> => {
  return useMutationQuery<Rule>({
    mutationFn: archiveRuleVersion,
    successMessage: 'Rule deactivated successfully.',
    errorMessage: 'Could not deactivate rule.',
    queryKey: siteID ? ruleQueryKeys.siteRoot(siteID) : ruleQueryKeys.all,
  });
};

export const useDeleteRuleSet = ({
  siteID,
}: {
  siteID?: string;
}): UseMutationResult<{ deleted: number }, Error> => {
  return useMutationQuery<{ deleted: number }>({
    mutationFn: deleteRuleSet,
    successMessage: 'Rule set deleted successfully.',
    errorMessage: 'Could not delete rule set.',
    queryKey: siteID ? ruleQueryKeys.siteRoot(siteID) : ruleQueryKeys.all,
  });
};
